import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Countdown from 'react-countdown';
import api from '../api/client';
import Toast, { useToast } from '../components/Toast';
import ConfirmDialog from '../components/ConfirmDialog';
import './ProductPage.css';

function CountdownRenderer({ days, hours, minutes, seconds, completed }) {
  if (completed) return <span style={{ color: 'var(--color-text-muted)' }}>Auction ended</span>;
  return (
    <span className={days === 0 && hours < 2 ? 'countdown-critical' : ''}>
      {days > 0 && `${days} ${days === 1 ? 'day' : 'days'} `}{hours.toString().padStart(2, '0')}:{minutes.toString().padStart(2, '0')}:{seconds.toString().padStart(2, '0')}
    </span>
  );
}

export default function ProductPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [bidAmount, setBidAmount] = useState('');
  const [bidError, setBidError] = useState('');
  const [authOpen, setAuthOpen] = useState(false);
  const [hiwOpen, setHiwOpen] = useState(false);
  const [maxBidInfoOpen, setMaxBidInfoOpen] = useState(false);
  const [bidConfirmOpen, setBidConfirmOpen] = useState(false);
  const { toast, showToast, hideToast } = useToast();
  const [activeImage, setActiveImage] = useState(0);
  const [thumbPage, setThumbPage] = useState(0);
  const THUMBS_PER_PAGE = 6;

  // Zoom loupe state
  const [zoomActive, setZoomActive] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(3);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [imgRect, setImgRect] = useState(null);
  const mainImageRef = useRef(null);

  const { data: product, isLoading } = useQuery({
    queryKey: ['product', id],
    queryFn: () => api.get(`/products/${id}`).then(r => r.data),
  });

  const { data: wishlistIds = [] } = useQuery({
    queryKey: ['wishlist-ids'],
    queryFn: () => api.get('/wishlist/ids').then(r => r.data),
  });

  const { data: recPrice } = useQuery({
    queryKey: ['recommended-price', product?.brand, product?.model, product?.grade_standardized, product?.size],
    queryFn: () => {
      const params = new URLSearchParams({ brand: product.brand });
      if (product.model) params.append('model', product.model);
      if (product.grade_standardized) params.append('grade', product.grade_standardized);
      if (product.size) params.append('size', product.size);
      if (product.product_type) params.append('form_code', product.product_type);
      return api.get(`/market-prices/lookup?${params}`).then(r => r.data);
    },
    enabled: !!product?.brand,
  });

  const rawImages = product?.images || [];
  const images = typeof rawImages === 'string' ? JSON.parse(rawImages) : rawImages;

  const prevImage = useCallback(() => setActiveImage(i => i > 0 ? i - 1 : images.length - 1), [images.length]);
  const nextImage = useCallback(() => setActiveImage(i => i < images.length - 1 ? i + 1 : 0), [images.length]);

  // Reset zoom on image change
  useEffect(() => { setZoomActive(false); }, [activeImage]);

  // Keyboard navigation
  useEffect(() => {
    const handler = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if (e.key === 'Escape') setZoomActive(false);
      if (e.key === 'ArrowLeft') prevImage();
      if (e.key === 'ArrowRight') nextImage();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [prevImage, nextImage]);

  const handleMouseEnter = () => {
    if (mainImageRef.current) {
      setImgRect(mainImageRef.current.getBoundingClientRect());
    }
    setZoomActive(true);
  };

  const handleMouseMove = (e) => {
    if (!mainImageRef.current) return;
    const rect = mainImageRef.current.getBoundingClientRect();
    setImgRect(rect);
    setMousePos({ x: e.clientX, y: e.clientY });
  };

  const handleMouseLeave = () => {
    setZoomActive(false);
    setImgRect(null);
  };

  const handleWheel = (e) => {
    if (!zoomActive) return;
    e.preventDefault();
    setZoomLevel(prev => Math.min(8, Math.max(1.5, prev + (e.deltaY < 0 ? 0.5 : -0.5))));
  };

  // Calculate lens and zoom positions
  const getLensData = () => {
    if (!imgRect) return null;
    const xRatio = (mousePos.x - imgRect.left) / imgRect.width;
    const yRatio = (mousePos.y - imgRect.top) / imgRect.height;

    // Clamp to image bounds
    const cx = Math.max(0, Math.min(1, xRatio));
    const cy = Math.max(0, Math.min(1, yRatio));

    // Lens size on the thumbnail (shows what portion is zoomed)
    const lensW = imgRect.width / zoomLevel;
    const lensH = imgRect.height / zoomLevel;

    return {
      cx, cy,
      lensLeft: cx * imgRect.width - lensW / 2,
      lensTop: cy * imgRect.height - lensH / 2,
      lensW, lensH,
      bgPosX: cx * 100,
      bgPosY: cy * 100,
    };
  };

  const lensData = zoomActive ? getLensData() : null;

  const toggleWishlist = useMutation({
    mutationFn: () => {
      if (wishlistIds.includes(id)) return api.delete(`/wishlist/${id}`);
      return api.post('/wishlist', { product_id: id });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['wishlist-ids'] }),
  });

  const placeBid = useMutation({
    mutationFn: () => api.post('/bids', { product_id: id, amount_euro: parseFloat(bidAmount) }),
    onSuccess: () => {
      showToast('Your bid has been placed successfully.');
      setBidError('');
      setBidAmount('');
      qc.invalidateQueries({ queryKey: ['product', id] });
    },
    onError: err => {
      const msg = err.response?.data?.detail || 'Your bid is too low. Please increase your amount.';
      setBidError(msg);
      showToast(msg, 'error');
    },
  });

  if (isLoading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}><div className="spinner" /></div>;
  if (!product) return <div>Product not found.</div>;

  const isB2C = product.user_type === 'individual';
  const price = isB2C ? (product.price_ttc || 0) : (product.price_ht || 0);
  const minBid = product.min_bid || price;
  const isWishlisted = wishlistIds.includes(id);

  const cap = (s) => {
    if (!s || typeof s !== 'string') return s;
    return s.charAt(0).toUpperCase() + s.slice(1);
  };

  // Build size display string
  const sizeDisplay = (() => {
    const parts = [];
    const named = product.size;
    const w = product.size_width_cm;
    const h = product.size_height_cm;
    const d = product.size_depth_cm;

    if (w || h || d) {
      const dims = [];
      if (w) dims.push(`W${w}`);
      if (h) dims.push(`H${h}`);
      if (d) dims.push(`D${d}`);
      const dimStr = dims.join(' \u00d7 ') + ' cm';
      if (named) return `${named} (${dimStr})`;
      return dimStr;
    }
    if (named) return named;
    return null;
  })();

  const details = [
    ['Type', cap(product.product_type)],
    ['Brand', product.brand],
    ['Model', cap(product.model)],
    ['Model number', product.model_number],
    ['Size', sizeDisplay],
    ['Color', cap(product.color)],
    ['Material', cap(product.material)],
    ['Grade', product.grade_standardized],
    ...(product.estimated_production_year && product.production_year_source === 'serial'
      ? [['Production year', product.estimated_production_year]]
      : product.estimated_production_year && product.production_year_source === 'estimated'
        ? [['Estimated era', product.estimated_production_year]]
        : []),
    ['Serial number', product.serial_number],
    ['Accessories', cap(product.accessories)],
  ].filter(([, v]) => v);

  return (
    <div className="product-page">
      <button className="back-btn" onClick={() => navigate(-1)}>&larr; Back</button>
      <div className="product-layout">
        <div className="product-images">
          <div
            className="main-image"
            onMouseEnter={handleMouseEnter}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            onWheel={handleWheel}
          >
            {images[activeImage] ? (
              <img
                ref={mainImageRef}
                src={images[activeImage]}
                alt={product.clean_title}
                style={{ cursor: zoomActive ? 'crosshair' : 'default' }}
                draggable={false}
              />
            ) : (
              <div className="no-img">No image</div>
            )}
            {zoomActive && lensData && (
              <div
                className="zoom-lens"
                style={{
                  left: lensData.lensLeft,
                  top: lensData.lensTop,
                  width: lensData.lensW,
                  height: lensData.lensH,
                }}
              />
            )}
            {images.length > 1 && (
              <>
                <button className="main-arrow main-arrow-left" onClick={prevImage}>&lsaquo;</button>
                <button className="main-arrow main-arrow-right" onClick={nextImage}>&rsaquo;</button>
              </>
            )}
          </div>
{/* zoom-result moved outside grid layout to avoid stacking context issues */}
          {images.length > 1 && (() => {
            const start = thumbPage * THUMBS_PER_PAGE;
            const hasMore = start + THUMBS_PER_PAGE < images.length;
            const visibleCount = hasMore ? THUMBS_PER_PAGE + 1 : images.length - start;
            const visible = images.slice(start, start + visibleCount);
            return (
              <div className="thumbnails-wrapper">
                {thumbPage > 0 && (
                  <button className="thumb-arrow thumb-arrow-left" onClick={() => setThumbPage(p => p - 1)}>&lsaquo;</button>
                )}
                <div className="thumbnails">
                  {visible.map((img, i) => {
                    const realIndex = start + i;
                    const isOverflow = hasMore && i === THUMBS_PER_PAGE;
                    return (
                      <button
                        key={realIndex}
                        className={`thumb ${realIndex === activeImage ? 'active' : ''} ${isOverflow ? 'thumb-peek' : ''}`}
                        onClick={() => isOverflow ? setThumbPage(p => p + 1) : setActiveImage(realIndex)}
                      >
                        <img src={img} alt="" />
                      </button>
                    );
                  })}
                </div>
                {hasMore && (
                  <button className="thumb-arrow thumb-arrow-right" onClick={() => setThumbPage(p => p + 1)}>&rsaquo;</button>
                )}
              </div>
            );
          })()}
        </div>

        <div className="product-info">
          <div className="product-brand">{product.brand}</div>
          <h1 className="product-title">{(product.clean_title || '').replace(new RegExp('^' + (product.brand || '') + '\\s*', 'i'), '')}</h1>

          <div className="grade-timer-row">
            {product.grade_standardized && (
              <span className={`grade-badge grade-${product.grade_standardized}`}>
                {product.grade_standardized}
              </span>
            )}
            {product.auction_date_end_france && (() => {
              const ended = new Date(product.auction_date_end_france) <= new Date();
              if (!ended) return (
                <div className="auction-timer">
                  <span className="timer-label">Closing in</span>
                  <Countdown date={new Date(product.auction_date_end_france)} renderer={CountdownRenderer} />
                </div>
              );
              if (product.auction_result === 'won') return (
                <div className="auction-result result-won">Won</div>
              );
              if (product.auction_result === 'lost') return (
                <div className="auction-result result-lost">Lost</div>
              );
              return (
                <div className="auction-result result-pending" title="Results may be published up to 48h after the closing of the auction">
                  Waiting for results
                </div>
              );
            })()}
          </div>

          <div className="price-block">
            <div className="price-label">Current bid{isB2C ? ' (incl. VAT)' : ' (excl. VAT)'}</div>
            <div className="price-value">&euro;{(product.best_bid ? Math.ceil(product.best_bid) : price).toLocaleString('fr-FR')}</div>
            {!isB2C && product.price_ttc && (
              <div className="current-bid">Incl. VAT: &euro;{(product.best_bid ? Math.ceil(product.best_bid * 1.20) : product.price_ttc).toLocaleString('fr-FR')}</div>
            )}
          </div>

          <div className="bid-section">
            <div className="bid-hint">Your maximum bid (minimum &euro;{minBid.toLocaleString('fr-FR')})</div>
            <div className="bid-input-row">
              <div style={{ position: 'relative' }}>
                <span className="currency-prefix">&euro;</span>
                <input
                  className="input bid-input"
                  type="number"
                  min={minBid}
                  step="1"
                  placeholder={minBid.toString()}
                  value={bidAmount}
                  onChange={e => setBidAmount(e.target.value)}
                  onFocus={e => { e.target.setAttribute('data-scroll', window.scrollY); setTimeout(() => window.scrollTo(0, parseInt(e.target.getAttribute('data-scroll') || 0)), 300); }}
                />
              </div>
              <button
                className="btn btn-primary"
                disabled={!bidAmount || placeBid.isPending}
                onClick={() => {
                  if (parseFloat(bidAmount) < minBid) {
                    setBidError(`Minimum bid is €${minBid.toLocaleString('fr-FR')}`);
                    showToast(`Minimum bid is €${minBid.toLocaleString('fr-FR')}`, 'error');
                    return;
                  }
                  setBidError('');
                  setBidConfirmOpen(true);
                }}
              >
                {placeBid.isPending ? <span className="spinner" /> : 'Place max bid'}
              </button>
            </div>
            {bidError && <div className="error-msg" style={{ marginTop: 8 }}>{bidError}</div>}
          </div>

          {isB2C && recPrice?.found && (
            <div className="recommended-price-section">
              <div className="rec-price-header">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                </svg>
                <span>Market reference</span>
              </div>
              <div className="rec-price-value">
                {isB2C
                  ? <>&euro;{recPrice.price_ttc?.toLocaleString('fr-FR')} <span className="rec-price-label">incl. VAT</span></>
                  : <>&euro;{recPrice.price_ht?.toLocaleString('fr-FR')} <span className="rec-price-label">excl. VAT</span></>
                }
              </div>
              <div className="rec-price-meta">
                Based on {recPrice.sample_size} similar sale{recPrice.sample_size > 1 ? 's' : ''} (last 30 days)
              </div>
            </div>
          )}

          <div className="how-it-works-section">
            <button className="hiw-toggle" onClick={() => setHiwOpen(!hiwOpen)}>
              <h3>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 8, verticalAlign: 'middle' }}>
                  <circle cx="12" cy="12" r="10" /><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" /><line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
                How it works
              </h3>
              <span className={`auth-chevron ${hiwOpen ? 'open' : ''}`}>&rsaquo;</span>
            </button>
            {hiwOpen && (
              <div className="hiw-content">
                <div className="hiw-steps">
                  <div className="hiw-step">
                    <span className="hiw-number">1</span>
                    <div>
                      <strong>Place your maximum bid</strong> — We bid progressively on your behalf at the Japanese auction.
                      <button className="info-toggle-link" onClick={(e) => { e.stopPropagation(); setMaxBidInfoOpen(!maxBidInfoOpen); }}>
                        {maxBidInfoOpen ? 'Hide details' : 'Why?'}
                      </button>
                      {maxBidInfoOpen && (
                        <div className="info-tooltip">
                          <ul>
                            <li>Auctions often close overnight — setting your max budget upfront ensures you don't miss out.</li>
                            <li>We won't place your full bid at once. We incrementally outbid other buyers on your behalf, only going as high as needed up to your maximum.</li>
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="hiw-step">
                    <span className="hiw-number">2</span>
                    <div><strong>We handle everything</strong> — Shipping from Japan, customs clearance, import duties</div>
                  </div>
                  <div className="hiw-step">
                    <span className="hiw-number">3</span>
                    <div><strong>Authentication</strong> — Every item verified before delivery, certificate included</div>
                  </div>
                  <div className="hiw-step">
                    <span className="hiw-number">4</span>
                    <div><strong>Delivered to you</strong> — Fully insured, door-to-door delivery</div>
                  </div>
                </div>
                <p className="hiw-footer">Your bid includes shipping, duties, our commission and all taxes.</p>
              </div>
            )}
          </div>

          <button className="btn btn-outline wishlist-toggle" onClick={() => toggleWishlist.mutate()}>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill={isWishlisted ? '#E74C3C' : 'none'}
              stroke={isWishlisted ? '#E74C3C' : 'currentColor'}
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ marginRight: 8, verticalAlign: 'middle' }}
            >
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
            </svg>
            {isWishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
          </button>

          <div className="authenticity-section">
            <button className="auth-toggle" onClick={() => setAuthOpen(!authOpen)}>
              <h3>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 8, verticalAlign: 'middle' }}>
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                  <polyline points="9 12 11 14 15 10" />
                </svg>
                Authenticity Guarantee
              </h3>
              <span className={`auth-chevron ${authOpen ? 'open' : ''}`}>&rsaquo;</span>
            </button>
            {authOpen && (
              <div className="auth-content">
                <div className="auth-points">
                  <div className="auth-point">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
                    <span>Every item inspected by luxury goods experts before delivery</span>
                  </div>
                  <div className="auth-point">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>
                    <span>Authenticity certificate included (LegitApp)</span>
                  </div>
                  <div className="auth-point">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>
                    <span>1-year guarantee — in the unlikely event of a non-authentic item, full refund guaranteed</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="details-section">
            <h3>Product details</h3>
            <table className="details-table">
              <tbody>
                {details.map(([label, value]) => (
                  <tr key={label}>
                    <td className="detail-label">{label}</td>
                    <td className="detail-value">{value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {(product.condition_notes || product.grade_standardized) && (() => {
            const gradeExplanations = {
              S: 'New or unused item',
              A: 'Excellent condition — minimal signs of use',
              AB: 'Very good condition — minor signs of use',
              B: 'Good condition — visible signs of use',
              BC: 'Fair condition — noticeable wear',
              C: 'Worn — significant signs of use',
              D: 'Functional issues — heavy wear',
            };
            const gradeText = gradeExplanations[product.grade_standardized] || '';
            const conditionText = product.condition_notes || '';
            return (
              <div className="condition-section">
                <h3>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 8, verticalAlign: 'middle' }}>
                    <circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" />
                  </svg>
                  Item condition
                </h3>
                {product.grade_standardized && (
                  <div className="condition-grade-line">
                    <span className={`grade-badge grade-${product.grade_standardized}`}>
                      {product.grade_standardized}
                    </span>
                    <span className="condition-grade-text">{gradeText}</span>
                  </div>
                )}
                {conditionText && (
                  <p className="condition-text">{conditionText}</p>
                )}
              </div>
            );
          })()}


        </div>
      </div>
      {zoomActive && lensData && images[activeImage] && imgRect && (
        <div
          className="zoom-result"
          style={{
            position: 'fixed',
            top: imgRect.top,
            left: imgRect.right + 16,
            width: imgRect.width,
            height: imgRect.height,
            backgroundImage: `url(${images[activeImage]})`,
            backgroundSize: `${imgRect.width * zoomLevel}px ${imgRect.height * zoomLevel}px`,
            backgroundPosition: `${lensData.bgPosX}% ${lensData.bgPosY}%`,
          }}
        />
      )}
      {toast && <Toast message={toast.message} type={toast.type} onClose={hideToast} />}
      <ConfirmDialog
        open={bidConfirmOpen}
        title="Confirm your bid"
        message={`By placing this bid of €${bidAmount || 0}, you commit to purchasing this item if your bid wins. This action is final and cannot be cancelled.`}
        confirmLabel="Confirm bid"
        onConfirm={() => { setBidConfirmOpen(false); placeBid.mutate(); }}
        onCancel={() => setBidConfirmOpen(false)}
      />
    </div>
  );
}
